import React, { useEffect, useMemo, useState } from 'react';
import {
  Backdrop,
  Box,
  CircularProgress,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';

const progressCardSx = {
  width: 'min(520px, calc(100vw - 32px))',
  borderRadius: 3,
  border: '1px solid #D5E2EE',
  p: { xs: 2.5, sm: 3 },
  boxShadow: '0 24px 64px rgba(6, 19, 32, 0.22)',
};

const phaseSets = {
  create: [
    'Guardando datos del empleado',
    'Revisando calendarios abiertos de la estación',
    'Preparando turnos S/A para asignación manual',
    'Finalizando alta',
  ],
  plannerCreate: [
    'Preparando datos de la estación y la posición',
    'Armando la planificación inicial del período',
    'Asignando turnos y descansos según las reglas vigentes',
    'Finalizando creación del calendario',
  ],
  update: [
    'Guardando cambios del empleado',
    'Revisando calendarios abiertos relacionados',
    'Aplicando actualización en calendarios activos',
    'Finalizando cambios',
  ],
  deactivate: [
    'Registrando la fecha de baja',
    'Revisando calendarios abiertos relacionados',
    'Retirando al empleado desde la fecha indicada',
    'Finalizando baja',
  ],
  activate: [
    'Registrando el nuevo período laboral',
    'Revisando calendarios abiertos relacionados',
    'Incorporando al empleado desde la fecha de reingreso',
    'Finalizando reactivación',
  ],
};

const CalendarSyncProgressOverlay = ({ open, variant = 'create' }) => {
  const phases = useMemo(() => phaseSets[variant] || phaseSets.create, [variant]);
  const [activeStep, setActiveStep] = useState(0);
  const description = variant === 'plannerCreate'
    ? 'Espere mientras generamos el calendario y validamos su planificación.'
    : 'Espere mientras guardamos la información y sincronizamos los calendarios abiertos.';
  const footer = variant === 'plannerCreate'
    ? 'Este proceso puede demorar unos segundos según el período, la posición y las validaciones a ejecutar.'
    : 'Este proceso puede demorar unos segundos según la cantidad de calendarios abiertos.';

  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      return undefined;
    }

    setActiveStep(0);

    const delays = [900, 2200, 3800];
    const timers = delays.map((delay, index) =>
      window.setTimeout(() => {
        setActiveStep((current) => Math.min(Math.max(current, index + 1), phases.length - 1));
      }, delay)
    );

    return () => timers.forEach((timerId) => window.clearTimeout(timerId));
  }, [open, phases.length]);

  return (
    <Backdrop
      open={open}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 2,
        backgroundColor: 'rgba(7, 17, 26, 0.52)',
        backdropFilter: 'blur(3px)',
      }}
    >
      <Paper elevation={0} sx={progressCardSx}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#16324F' }}>
          Espere un momento
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          {description}
        </Typography>

        <LinearProgress
          sx={{
            mt: 2.5,
            mb: 2.5,
            height: 8,
            borderRadius: 999,
            backgroundColor: '#E7EEF5',
          }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {phases.map((phase, index) => {
            const isCompleted = index < activeStep;
            const isActive = index === activeStep;

            return (
              <Box
                key={phase}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 2,
                  backgroundColor: isActive ? '#EDF6FF' : '#F8FBFD',
                  border: isActive ? '1px solid #B7D6F6' : '1px solid #E5EDF4',
                }}
              >
                {isCompleted ? (
                  <CheckCircleRoundedIcon sx={{ color: '#2E8B57' }} />
                ) : isActive ? (
                  <CircularProgress size={20} thickness={5} />
                ) : (
                  <RadioButtonUncheckedRoundedIcon sx={{ color: '#9AA9B8' }} />
                )}

                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: isActive || isCompleted ? '#16324F' : '#6B7B8C' }}
                  >
                    {phase}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6B7B8C' }}>
                    {isCompleted
                      ? 'Completado'
                      : isActive
                        ? 'En curso'
                        : 'Pendiente'}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Typography variant="caption" sx={{ display: 'block', mt: 2.5, color: '#6B7B8C' }}>
          {footer}
        </Typography>
      </Paper>
    </Backdrop>
  );
};

export default CalendarSyncProgressOverlay;
